// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback } from 'react'
import { GripVertical, Plus, X } from 'lucide-react'
import {
  Button,
  Input,
  Label,
  Textarea,
} from '@/components/ui'

export type FormatType = 'table' | 'bullets' | 'prose'

export interface FormatSpecEditorProps {
  formatType: FormatType
  formatSpec: string[]
  formatGuidance: string
  disabled?: boolean
  onFormatSpecChange: (spec: string[]) => void
  onFormatGuidanceChange: (guidance: string) => void
}

/**
 * Dynamic format spec editor based on format type:
 * - Table: ordered list of column names
 * - Bullets: ordered list of points to extract
 * - Prose/Callout: guidance textarea
 */
export function FormatSpecEditor({
  formatType,
  formatSpec,
  formatGuidance,
  disabled = false,
  onFormatSpecChange,
  onFormatGuidanceChange,
}: FormatSpecEditorProps): JSX.Element {
  const handleAddItem = useCallback(() => {
    onFormatSpecChange([...formatSpec, ''])
  }, [formatSpec, onFormatSpecChange])

  const handleRemoveItem = useCallback((index: number) => {
    onFormatSpecChange(formatSpec.filter((_, i) => i !== index))
  }, [formatSpec, onFormatSpecChange])

  const handleUpdateItem = useCallback((index: number, value: string) => {
    const updated = [...formatSpec]
    updated[index] = value
    onFormatSpecChange(updated)
  }, [formatSpec, onFormatSpecChange])

  const handleMoveItem = useCallback((fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= formatSpec.length) return
    const updated = [...formatSpec]
    const [item] = updated.splice(fromIndex, 1)
    updated.splice(toIndex, 0, item)
    onFormatSpecChange(updated)
  }, [formatSpec, onFormatSpecChange])

  if (formatType === 'prose') {
    return (
      <div className="space-y-2">
        <Label htmlFor="format-guidance">Format Guidance (optional)</Label>
        <Textarea
          id="format-guidance"
          value={formatGuidance}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onFormatGuidanceChange(e.target.value)}
          placeholder="e.g., Focus on financial implications and key dates"
          disabled={disabled}
          rows={3}
          className="resize-none"
        />
        <p className="text-sm text-slate-600">
          Additional guidance for how to format the prose output.
        </p>
      </div>
    )
  }

  const label = formatType === 'table' ? 'Table Columns' : 'Bullet Points'
  const itemLabel = formatType === 'table' ? 'column' : 'point'
  const placeholder = formatType === 'table'
    ? 'e.g., Metric, Definition, Value'
    : 'e.g., Key finding, Impact, Recommendation'

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddItem}
          disabled={disabled}
          className="h-7 px-2 text-xs"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add {itemLabel}
        </Button>
      </div>

      {formatSpec.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border/70 bg-muted/10 py-6 text-center text-sm text-slate-600">
          No {itemLabel}s defined. Click "Add {itemLabel}" to get started.
          <br />
          <span className="text-sm text-slate-600">{placeholder}</span>
        </div>
      ) : (
        <div className="space-y-2">
          {formatSpec.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="flex flex-col gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleMoveItem(index, index - 1)}
                  disabled={disabled || index === 0}
                  className="h-4 w-6 p-0 opacity-50 hover:opacity-100"
                  title="Move up"
                >
                  <span className="text-[10px]">▲</span>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleMoveItem(index, index + 1)}
                  disabled={disabled || index === formatSpec.length - 1}
                  className="h-4 w-6 p-0 opacity-50 hover:opacity-100"
                  title="Move down"
                >
                  <span className="text-[10px]">▼</span>
                </Button>
              </div>
              <GripVertical className="h-4 w-4 text-slate-600/40" />
              <span className="text-sm text-slate-600 w-4">{index + 1}.</span>
              <Input
                value={item}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateItem(index, e.target.value)}
                placeholder={`${formatType === 'table' ? 'Column' : 'Point'} name...`}
                disabled={disabled}
                className="flex-1"
              />
              <Button
                aria-label={`Remove ${formatType === 'table' ? 'column' : 'point'}`}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveItem(index)}
                disabled={disabled}
                className="h-8 w-8 p-0 text-slate-600 hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <p className="text-sm text-slate-600">
        {formatType === 'table'
          ? 'Define columns in order. The LLM will generate a markdown table with these exact columns.'
          : 'Define points to cover. The LLM will generate a bullet list addressing each point.'}
      </p>
    </div>
  )
}
